import api from "../utils/axiosConfig";

export type SignUpParams = {
  referral_code: string;
  username: string;
  password: string;
  full_name: string;
  phone_no: string;
  bank_name: string;
  bank_account_no: string;
};

export type RegistrationStatus = "PENDING_APPROVAL" | "APPROVED" | "REJECTED";

export type SignUpResponse = {
  registration_id: string;
  username: string;
  full_name: string;
  status: RegistrationStatus;
  submitted_at: string;
};

export type SignUpApiResponse = {
  status: number;
  system: string;
  message: string;
  data: SignUpResponse;
};

/**
 * Registers a player.
 *
 * `locale` is the language the form was filled in, and it matters twice: the
 * server answers with the review notice in that language — the notice is
 * operator-written copy that only exists per language, so without this a player
 * who chose 中文 got a Chinese dialog wrapped around an English sentence — and it
 * is stored on the account, so every later notice reaches them in it too.
 */
export const postSignUp = async (
  params: SignUpParams & { password_confirmation: string },
  locale: "en" | "zh" = "en"
): Promise<SignUpApiResponse> => {
  const response = await api.post<{
    status: string;
    username: string;
    notice: string;
    requiresReview: boolean;
  }>("/auth/register", {
    referralCode: params.referral_code,
    username: params.username,
    password: params.password,
    confirmPassword: params.password_confirmation,
    fullName: params.full_name,
    phone: params.phone_no || undefined,
    // Sent, not dropped: the form asks for these and the backoffice shows the
    // approver what the applicant typed — a field collected here and discarded
    // on the way out is one nobody can ever check against.
    bankName: params.bank_name || undefined,
    bankAccountNo: params.bank_account_no || undefined,
    locale,
  });

  return {
    status: 1,
    system: "auth",
    message: response.data.notice,
    data: {
      registration_id: "",
      username: response.data.username,
      full_name: params.full_name,
      status: "PENDING_APPROVAL",
      submitted_at: new Date().toISOString(),
    },
  };
};
